import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QAgentHome, QAgentChat, QAgentProjectChat, QAgentSidebar } from "../components/qagent";
import LoginPage from "../components/login";
import RegisterPage from "../components/register";
import ForgotPasswordPage from "../components/forgot-password";
import ResetPasswordPage from "../components/reset-password";
import FeatureTogglesPage from "../components/feature-toggles";
import RAGPage from "../components/rag";
import PlaybooksPage from "../components/playbooks";
import PlaybookDetail from "../components/playbooks/PlaybookDetail";
import MeetingDetailPage from "../components/meeting-detail";
import FileSearchStoresPage from "../components/file-search-stores";
import StoreDetailsPage from "../components/file-search-stores/StoreDetailsPage";
import FeedbackPage from "../components/feedback";
import FeedbackListPage from "../components/feedback/FeedbackListPage";
import UsageDashboard from "../components/admin/UsageDashboard";
import ModelSettings from "../components/admin/ModelSettings";
import OrganizationsDashboard from "../components/admin/OrganizationsDashboard";
import OrganizationDetail from "../components/admin/OrganizationDetail";
import MembersPage from "../components/members/MembersPage";
import ComparisonPage from "../components/comparisons/ComparisonPage";
import ComparePage from "../components/comparisons/ComparePage";
import CostPage from "../components/cost/CostPage";
import AccountPage from "../components/account/AccountPage";
import SubscriptionExpiredPage from "../components/subscription-expired";
import TrialBanner from "../components/trial-banner";
import NotFoundPage from "../components/not-found";
import { NavigateFunctionComponent } from "../http-client/InterceptorComponent";
import ProtectedRoute from "./ProtectedRoute";
import FeatureFlagRoute from "./FeatureFlagRoute";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "../components/ui/sidebar";
import logo from "../assets/logo.png";
import { useBoundStore } from "../store";
import { getOwnUser } from "../services/User";
import { OnboardingController } from "../onboarding/OnboardingController";
import { WorkspaceProvider, useWorkspace } from "../workspace/WorkspaceProvider";

/**
 * Refreshes the persisted user object on app start so stale cached
 * subscription/exemption fields don't cause incorrect redirects.
 */
const UserRefresher: React.FC = () => {
  const accessToken = useBoundStore((state) => state.accessToken);
  const setUser = useBoundStore((state) => state.setUser);

  useEffect(() => {
    if (!accessToken) return;
    getOwnUser().then((res) => {
      if (res.success && res.data) {
        setUser(res.data);
      }
    });
  }, [accessToken, setUser]);

  return null;
};

// Single layout with merged QAgent sidebar for all authenticated routes
const AuthenticatedLayout = ({ children }: { children: React.ReactNode }) => {
  const { displayName, logoUrl } = useWorkspace();
  return (
  <SidebarProvider>
    <QAgentSidebar />
    <SidebarInset>
      {/* On mobile the sidebar collapses to an off-canvas drawer; the only
          trigger lives inside it, so expose one here. Hidden on >=md where the
          sidebar is always visible. */}
      <header className="md:hidden sticky top-0 z-10 flex items-center gap-2 border-b bg-background px-3 py-2">
        <SidebarTrigger />
        {logoUrl ? (
          <img src={logoUrl} alt={displayName ?? "Workspace"} className="h-6 max-w-[140px] object-contain" />
        ) : displayName ? (
          <span className="text-lg font-semibold text-primary">{displayName}</span>
        ) : (
          <img src={logo} alt="Queri" className="h-6" />
        )}
      </header>
      <div className="flex-1 p-4">
        <TrialBanner />
        {children}
      </div>
    </SidebarInset>
  </SidebarProvider>
  );
};

const Router = () => (
  <BrowserRouter>
    <NavigateFunctionComponent />
    <UserRefresher />
    <OnboardingController />
    <WorkspaceProvider>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register/:signUpCode" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/subscription-expired" element={<SubscriptionExpiredPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <QAgentHome />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/qagent/project/:projectId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <QAgentProjectChat />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/qagent/:chatId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <QAgentChat />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route path="/users" element={<Navigate to="/admin/organizations" replace />} />
      <Route
        path="/features"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FeatureFlagRoute featureKey="feature-toggles-admin">
                <FeatureTogglesPage />
              </FeatureFlagRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/knowledge-database"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FeatureFlagRoute featureKey="knowledge-database-admin">
                <RAGPage />
              </FeatureFlagRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/playbooks"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <PlaybooksPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/playbooks/:id"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <PlaybookDetail />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/meetings/:meetingId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FeatureFlagRoute featureKey="ai-meeting-assistant">
                <MeetingDetailPage />
              </FeatureFlagRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/file-search-stores"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FeatureFlagRoute featureKey="knowledge-database-admin">
                <FileSearchStoresPage />
              </FeatureFlagRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/file-search-stores/:storeId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FeatureFlagRoute featureKey="knowledge-database-admin">
                <StoreDetailsPage />
              </FeatureFlagRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/compare"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ComparePage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route path="/comparisons" element={<Navigate to="/compare" replace />} />
      <Route
        path="/comparisons/:id"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ComparisonPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/members"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <MembersPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/cost"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <CostPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <AccountPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/feedback"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FeedbackPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/feedback/admin"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FeatureFlagRoute featureKey="queri-admin">
                <FeedbackListPage />
              </FeatureFlagRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/admin/usage"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FeatureFlagRoute featureKey="queri-admin">
                <UsageDashboard />
              </FeatureFlagRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route path="/admin/trials/users" element={<Navigate to="/admin/organizations" replace />} />
      <Route
        path="/admin/organizations"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FeatureFlagRoute featureKey="queri-admin">
                <OrganizationsDashboard />
              </FeatureFlagRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/admin/organizations/:organizationId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FeatureFlagRoute featureKey="queri-admin">
                <OrganizationDetail />
              </FeatureFlagRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="/admin/models"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FeatureFlagRoute featureKey="model-settings-admin">
                <ModelSettings />
              </FeatureFlagRoute>
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <NotFoundPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />
    </Routes>
    </WorkspaceProvider>
  </BrowserRouter>
)

export default Router;
